import React, { createContext, useEffect, useMemo } from "react";
import { createBrowserRouter } from "react-router-dom";
import { appRoutes } from "./routes/appRoutes";
import axios from "axios";
import { endpoints } from "../endpoints/endpoints";
import { useSnackbar } from "notistack";

export const AppContext = createContext<any>({});

export interface User {
  sessionId: string;
  sessionValidUntil: string;
  id: number | string;
  gender: {
    label: string;
    value: string;
  } | null;
  firstName: string;
  lastName: string;
  role: {
    label: string;
    value: string;
  } | null;
  email: string;
  location: {
    label: string;
    value: string;
  } | null;
  phoneNumber: string;
  startOfQualification: string;
  endOfQualification: string;
  qualification: string;
  resetPassword: number;
  isSpeedCoach: number;
}

export interface IAppointmentRating {
  sterneZusammenarbeit: number;
  sterneOffenenFragenKlaeren: number;
  sterneBewerbungsprozess: number;
  sterneKompetenzen: number;
  sterneBewerbungsunterlagen: number;
  sterneWeiterempfehlen: number;
  freitext: string;
  themenBesprechen: string;
  themenAngebote: string;
  averageRating: number;
}

export interface Appointment {
  id: string;
  date: string;
  start: string;
  end: string;
  day: string;
  type: string;
  coach: string;
  href: string | null;
  status: string;
  meta: {
    today: boolean;
    past: boolean;
    missed: boolean;
    stornoPossible: boolean;
    rating: null | IAppointmentRating;
  };
}

const AppContextProvider = ({
  children,
}: {
  children: React.JSX.Element;
}): React.JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const [openLoginDialog, setOpenLoginDialog] = React.useState(false);
  const [openForegtPasswordDialog, setOpenForegtPasswordDialog] =
    React.useState(false);

  const [colorMode, setColorMode] = React.useState<"light" | "dark">("light");

  const [user, setUser] = React.useState<User>({} as User);

  const refreshUser = React.useRef(false);

  const [routes, setRoutes] = React.useState<any>(
    createBrowserRouter(appRoutes.open)
  );

  useEffect(() => {
    if (refreshUser.current) return;

    const storedSessionId = localStorage.getItem("sessionId");

    if (storedSessionId) {
      refreshUser.current = true;
      axios
        .post(endpoints.authentication.refresh, { sessionId: storedSessionId })
        .then((res) => {
          if (res.data.data) {
            setUser(res.data.data);
          } else {
            enqueueSnackbar("Du wurdest automatisch abgemeldet!", {
              variant: "error",
            });
            localStorage.removeItem("sessionId");
            setUser({} as User);
          }

          refreshUser.current = false;
        })
        .catch((err) => {
          console.log(err);
          refreshUser.current = false;
        });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    switch (user.role?.value) {
      // participant case
      case "676a0d5f-e974-4321-80ba-d464ca3f1df9":
        setRoutes(
          createBrowserRouter([...appRoutes.open, ...appRoutes.participant])
        );
        break;

      // coach case
      case "c751ded5-7446-4d9c-8ff6-20e95ebaf716":
        setRoutes(createBrowserRouter([...appRoutes.open, ...appRoutes.coach]));
        break;

      // admin case
      case "8779046b-1003-42a4-982a-a676d0c6d268":
        setRoutes(
          createBrowserRouter([
            ...appRoutes.open,
            ...appRoutes.participant,
            ...appRoutes.coach,
            ...appRoutes.admin,
          ])
        );

        break;

      default:
        setRoutes(createBrowserRouter(appRoutes.open));
        break;
    }
  }, [user.role]);

  const handleLogout = (): void => {
    localStorage.removeItem("sessionId");
    setUser({} as User);
  };

  // if the response is an error and the message is "session invalid" the user will be logged out
  // there is no response pattern. The response can be different depending on the request
  const checkSession = React.useCallback((response: any): void => {
    if (
      response.data?.meta?.status === "error" &&
      response.data?.meta?.message === "session invalid"
    ) {
      console.log("session invalid");
      enqueueSnackbar("Ihre Session ist abgelaufen", {
        variant: "error",
      });
      handleLogout();
    }
  }, [enqueueSnackbar]);

  const userAgent = navigator.userAgent;
  const userData = React.useRef({});

  React.useEffect(() => {
    if (Object.keys(userData.current).length > 0) return;

    userData.current = {
      userAgent: userAgent,
      userLanguage: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };

    axios.post(endpoints.analytics.userAgent, userData.current);
  }, [userAgent]);

  const value: {
    user: User;
    setUser: React.Dispatch<React.SetStateAction<User>>;
    openLoginDialog: boolean;
    setOpenLoginDialog: React.Dispatch<React.SetStateAction<boolean>>;
  } = useMemo(
    () => ({
      user,
      setUser,
      openLoginDialog,
      setOpenLoginDialog,
      routes,
      handleLogout,
      colorMode,
      setColorMode,
      openForegtPasswordDialog,
      setOpenForegtPasswordDialog,
      checkSession,
    }),
    [
      user,
      openLoginDialog,
      routes,
      colorMode,
      openForegtPasswordDialog,
      checkSession,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
